<script setup lang="ts">
/**
 * Filter drawer for the refund work queue. No state field — the queue
 * is locked to PENDING. Pinned "Apply filters" CTA at the bottom per
 * `.claude/rules/ui-kit-theme.md → Drawer chrome for form drawers`.
 */
import { reactive, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Button from '@uiKit/elements/Button.vue'
import AppDrawer from '@uiKit/elements/Drawer.vue'
import Input from '@uiKit/elements/Input.vue'
import DatePicker from '@uiKit/elements/DatePicker.vue'
import DrawerHeader from '@uiKit/components/DrawerHeader.vue'
import FormField from '@uiKit/components/FormField.vue'

export interface RefundsFilterValues {
  fromDate: Date | null
  toDate: Date | null
  investorName: string
  investorEmail: string
}

interface Props {
  visible: boolean
  values: RefundsFilterValues
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'apply', values: RefundsFilterValues): void
  (e: 'reset'): void
}>()

const draft = reactive<RefundsFilterValues>({
  fromDate: props.values.fromDate,
  toDate: props.values.toDate,
  investorName: props.values.investorName,
  investorEmail: props.values.investorEmail,
})

watch(
  () => props.visible,
  (isVisible) => {
    if (!isVisible) return
    draft.fromDate = props.values.fromDate
    draft.toDate = props.values.toDate
    draft.investorName = props.values.investorName
    draft.investorEmail = props.values.investorEmail
  },
)

function handleApply() {
  emit('apply', {
    fromDate: draft.fromDate,
    toDate: draft.toDate,
    investorName: draft.investorName.trim(),
    investorEmail: draft.investorEmail.trim(),
  })
  emit('update:visible', false)
}

function handleReset() {
  draft.fromDate = null
  draft.toDate = null
  draft.investorName = ''
  draft.investorEmail = ''
  emit('reset')
}

function handleClose() {
  emit('update:visible', false)
}
</script>

<template>
  <AppDrawer
    :model-value="visible"
    position="right"
    width="440px"
    :show-close-icon="false"
    @update:model-value="(v) => !v && handleClose()"
  >
    <template #header>
      <DrawerHeader title="Filter refunds" subtitle="Refine the queue" @close="handleClose">
        <template #extras>
          <Button label="Reset" variant="text" color="secondary" @click="handleReset" />
        </template>
      </DrawerHeader>
    </template>

    <Box flex direction="col" class="drawer-body">
      <Box flex direction="col" gap="4" class="drawer-body__content">
        <FormField label="From">
          <DatePicker v-model="draft.fromDate" placeholder="DD/MM/YYYY" date-format="dd/mm/yy" />
        </FormField>
        <FormField label="To">
          <DatePicker v-model="draft.toDate" placeholder="DD/MM/YYYY" date-format="dd/mm/yy" />
        </FormField>
        <FormField label="Investor name">
          <Input v-model="draft.investorName" placeholder="Name contains…" />
        </FormField>
        <FormField label="Investor email">
          <Input v-model="draft.investorEmail" placeholder="Email contains…" />
        </FormField>
      </Box>

      <Box class="drawer-body__actions">
        <Button label="Apply filters" color="primary" block size="large" @click="handleApply" />
      </Box>
    </Box>
  </AppDrawer>
</template>

<style scoped>
.drawer-body {
  min-height: 100%;
  padding: 1.25rem 1.5rem;
}

.drawer-body__content {
  flex: 1;
}

.drawer-body__actions {
  margin-top: auto;
  padding-top: 1rem;
}

@media (max-width: 992px) {
  .drawer-body {
    padding: 1rem;
  }
}
</style>
